'use client';

import { MouseEvent, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { cn } from '@utils/cn';
import { getI18nLinkHref } from '@services/i18nPaths';
import { MainMenuLink } from '@type/header-types';
import {
  AddTaskInfo,
  AddTaskItem,
  trackAddJobClick,
  trackAddTaskClick,
  trackAddTaskInfoOpen,
  useAddTaskItems,
} from '@components/layout/addTask';
import { MainNavbarLink } from './MainNavbarLink';

interface AddJobMenuProps {
  link: MainMenuLink;
  locale?: Locale;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

/**
 * "Add job" in the navbar, with a hover menu offering the two zmeu task
 * entry points alongside it.
 *
 * The trigger stays an ordinary link: hovering reveals the menu, clicking still
 * goes to the add-job form exactly as before. That is why this is not a
 * Headless UI Popover — a Popover button opens on click and would swallow the
 * navigation, which would quietly break the most-used action in the navbar to
 * add a secondary one.
 *
 * Desktop only, and that is enough: the header's link row is hidden below lg,
 * and mobile gets the same choices from the bottom bar's add-job cell, which
 * opens on tap because hover does not exist there.
 */
export const AddJobMenu = ({ link, locale, onClick }: AddJobMenuProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  // The banner belongs to an open menu: leaving the menu has to take it with it,
  // or it would be showing again the next time the menu is hovered.
  const closeMenu = () => {
    setOpen(false);
    setInfoOpen(false);
  };

  const [callItem, ...otherItems] = useAddTaskItems();

  const renderTaskLink = ({
    href, channel, icon: Icon, ariaLabel, dataTestId, external,
  }: AddTaskItem) => (
    <a
      key={dataTestId}
      href={href}
      role="menuitem"
      title={ariaLabel}
      aria-label={ariaLabel}
      data-test-id={dataTestId}
      onClick={() => trackAddTaskClick('navbar', channel)}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="text-green-600 hover:text-primary"
    >
      <Icon className="size-5 shrink-0" />
    </a>
  );

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={closeMenu}
      // Keyboard users never fire mouseenter, so the same menu opens on focus
      // anywhere inside and closes when focus leaves.
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) closeMenu();
      }}
    >
      <MainNavbarLink
        type={link.type}
        route={link.route}
        label={link.label}
        icon={link.icon}
        locale={locale}
        onClick={(event) => {
          trackAddJobClick('navbar', 'trigger');
          // MainNavbarLink types its handler as MouseEventHandler<Element>; the prop
          // here is the narrower HTMLElement form the caller passes in.
          onClick?.(event as MouseEvent<HTMLElement>);
        }}
        dataTestId={link.dataTestId}
      />

      <div
        className={cn(
          'absolute right-0 top-full z-50 min-w-max overflow-hidden rounded-md bg-surface shadow-lg ring-1 ring-ink/10',
          open ? 'block' : 'hidden',
        )}
        role="menu"
        aria-hidden={!open}
      >
        {/* The add-job route, listed alongside the zmeu entries so the menu reads
            as a complete set of choices rather than "the other two things". */}
        <a
          href={getI18nLinkHref(link.route, locale as Locale)}
          role="menuitem"
          data-test-id="navbar-add-job-menu-item"
          onClick={() => trackAddJobClick('navbar', 'menu')}
          className="flex items-center gap-5 whitespace-nowrap px-4 py-2.5 text-sm font-semibold text-ink hover:bg-surface-50 hover:text-primary"
        >
          {/* Empty gutter the size of the Task row's leading icon, so both labels start
              in the same column instead of the icon indenting only one. The gap here has
              to stay in step with the Task row's, or the two labels drift apart again. */}
          <span aria-hidden className="size-5 shrink-0" />
          {t('label.job')}
        </a>

        {/* A single "Task" row: the two zmeu entry points flank the wording, each icon
            its own link so the click target says which channel it opens, and the
            wording itself opens the explanation banner below. */}
        <div className="flex items-center gap-5 whitespace-nowrap border-t border-ink/5 px-4 py-2.5 text-sm font-semibold text-ink">
          {callItem && renderTaskLink(callItem)}
          {/* The wording explains zmeu rather than opening a channel: the two icons
              already carry the actions, so the label is where the "what is this?"
              question gets answered. */}
          <button
            type="button"
            role="menuitem"
            aria-expanded={infoOpen}
            data-test-id="nav-add-task-info"
            onClick={() => setInfoOpen((wasOpen) => {
              if (!wasOpen) trackAddTaskInfoOpen('navbar');
              return !wasOpen;
            })}
            className="cursor-pointer font-semibold text-ink hover:text-primary"
          >
            {t('label.task')}
          </button>
          {otherItems.map(renderTaskLink)}
        </div>

        {infoOpen && <AddTaskInfo surface="navbar" />}
      </div>
    </div>
  );
};
